/**
 * Что человек уже видел: подсказки первого входа и «Что нового» (§10, M5).
 *
 * Хранится на сервере вместе с профилем — не в localStorage. Няня открывает
 * приложение с телефона, иногда с планшета мужа, иногда из Telegram Desktop;
 * это три разных хранилища браузера, и подсказка вылезала бы в каждом заново.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queries'
import type { Me, Role } from '@/shared/api/types'
import { useSession } from '@/app/session'

/** Ключи подсказок. Должны совпадать с `COACH_KEYS` в backend/app/services/hints.py. */
export type CoachKey =
  | 'parent_day'
  | 'parent_pay'
  | 'parent_family'
  | 'nanny_shift'
  | 'nanny_tiles'
  | 'nanny_earnings'

/**
 * Подсказки по ролям, в порядке показа.
 *
 * Лежат здесь, а не рядом с текстами в CoachTour: список нужен и «Что нового»,
 * чтобы понять, прошёл ли человек тур, а импорт из компонента в модуль данных
 * замкнул бы зависимости в кольцо.
 */
export const COACH_KEYS_BY_ROLE: Record<Role, CoachKey[]> = {
  parent: ['parent_day', 'parent_pay', 'parent_family'],
  nanny: ['nanny_shift', 'nanny_tiles', 'nanny_earnings'],
}

export const COACH_KEYS: CoachKey[] = [
  ...COACH_KEYS_BY_ROLE.parent,
  ...COACH_KEYS_BY_ROLE.nanny,
]

interface HintsOut {
  hints_seen: string[]
}

/**
 * Отметить подсказки показанными.
 *
 * Ответ сервера кладём и в кэш `/me`, и в стор: подсказка обязана исчезнуть
 * сразу, а не после следующего запроса профиля. Idempotency-Key не нужен —
 * на сервере это объединение множеств, повтор ничего не меняет.
 */
export function useMarkHints() {
  const client = useQueryClient()
  const setMe = useSession((state) => state.setMe)

  return useMutation<HintsOut, ApiError, string[]>({
    mutationFn: (keys) => api.post<HintsOut>('/me/hints', { keys }),
    onSuccess: (result) => {
      const previous = client.getQueryData<Me>(queryKeys.me)
      if (!previous) return
      const next: Me = { ...previous, hints_seen: result.hints_seen }
      client.setQueryData(queryKeys.me, next)
      setMe(next)
    },
  })
}

/** Ключи, уже отмеченные показанными. */
export function useSeenHints(): Set<string> {
  const seen = useSession((state) => state.me?.hints_seen)
  return new Set(seen ?? [])
}
